import IconButton from 'components/common/iconButton/IconButton'
import OsmMapPreview from 'components/common/OsmMapPreview'
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, IconSizes } from 'types/general'
import { GeoPoint } from 'types/osmAddress'

type ListingMapPreviewPopupProps = {
  isOpen: boolean
  onClose: () => void
  location: GeoPoint | null
  addressLine?: string
}

const ListingMapPreviewPopup = ({
  isOpen,
  onClose,
  location,
  addressLine,
}: ListingMapPreviewPopupProps) => {
  const { t } = useTranslation()

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen || !location) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={event => {
        event.stopPropagation()
        onClose()
      }}
      role="presentation"
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-label={t('propertyCard.mapPreviewTitle')}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <span className="text-sm font-semibold text-imovSecondary">
            {t('propertyCard.mapPreviewTitle')}
          </span>
          <IconButton
            icon="regular-circle-xmark"
            size={IconSizes.LG}
            color={CustomColorsRef.Primary}
            hoverColor={CustomColorsRef.Danger}
            handleClick={onClose}
          />
        </div>
        <OsmMapPreview
          location={location}
          height="220px"
          className="rounded-none border-0 border-b border-gray-100"
        />
        {addressLine ? (
          <p className="px-4 py-3 text-sm leading-snug text-gray-600">{addressLine}</p>
        ) : null}
      </div>
    </div>,
    document.body
  )
}

export default ListingMapPreviewPopup
